import Loading from "@/src/components/Loading";
import { DefaultRoleTag } from "@/src/components/permissions/DefaultRoleTag";
import { DealPermissionsSummary } from "@/src/components/permissions/DealPermissionsSummary";
import {
  SettingsCard,
  SettingsEmpty,
  SettingsPage,
  SettingsRow,
} from "@/src/components/settings/SettingsLayout";
import { toasts } from "@/src/components/toasts/toasts";
import { Button } from "@/src/components/ui/button";
import {
  DealRolesQuery,
  DealRoleType,
  useDealRolesQuery,
  useUpdateDealRoleMutation,
} from "@/src/graphql/generated";
import useGqlClient from "@/src/hooks/useGqlClient";
import { useQueryClient } from "@tanstack/react-query";
import { PlusIcon } from "lucide-react";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { CreateDealRoleModal } from "./CreateDealRoleModal";
import { EditDealRoleModal } from "./EditDealRoleModal";

type Role = DealRolesQuery["dealRoles"][number];

export function RolesSettings() {
  const { dealId } = useParams<{ dealId: string }>();
  const client = useGqlClient();

  const dealRolesQuery = useDealRolesQuery(
    client,
    {
      dealId,
      input: {
        roleTypes: [DealRoleType.Internal, DealRoleType.Guest],
        resourceTypes: [],
      },
    },
    {
      enabled: dealId !== "",
    },
  );

  if (dealRolesQuery.error) {
    return (
      <p className="text-sm text-muted-foreground">Failed to load roles</p>
    );
  }

  if (dealRolesQuery.isLoading || !dealRolesQuery.data) {
    return <Loading />;
  }

  const roles = dealRolesQuery.data.dealRoles;
  const internalRoles = roles.filter((r) => r.type === DealRoleType.Internal);
  const guestRoles = roles.filter((r) => r.type === DealRoleType.Guest);

  return (
    <SettingsPage
      title="Roles"
      description="What each role can do on this deal."
    >
      <RoleSection
        title="Admin roles"
        emptyLabel="admin"
        roleType={DealRoleType.Internal}
        roles={internalRoles}
        dealId={dealId}
        allowDefault
      />
      <RoleSection
        title="Guest roles"
        emptyLabel="guest"
        roleType={DealRoleType.Guest}
        roles={guestRoles}
        dealId={dealId}
      />
    </SettingsPage>
  );
}

function RoleSection(props: {
  title: string;
  emptyLabel: string;
  roleType: DealRoleType;
  roles: Role[];
  dealId: string;
  allowDefault?: boolean;
}) {
  const client = useGqlClient();
  const queryClient = useQueryClient();
  const updateRole = useUpdateDealRoleMutation(client);

  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  async function makeDefault(role: Role) {
    if (updateRole.isPending || role.isDefault) {
      return;
    }

    try {
      await updateRole.mutateAsync({
        input: {
          id: role.id,
          isDefault: true,
        },
      });
      await queryClient.invalidateQueries({ queryKey: ["DealRoles"] });
      toasts.success("Default role updated");
    } catch {
      toasts.error("Failed to update default role");
    }
  }

  return (
    <>
      <SettingsCard
        title={props.title}
        description={
          props.emptyLabel === "admin"
            ? "Roles for admin group members"
            : "Roles for guest group members"
        }
        action={
          <Button type="button" size="sm" onClick={() => setShowCreate(true)}>
            <PlusIcon className="mr-1.5 h-3.5 w-3.5" />
            Create
          </Button>
        }
        contentClassName="divide-y divide-border p-0"
      >
        {props.roles.length === 0 ? (
          <SettingsEmpty>
            No {props.emptyLabel} roles configured.
          </SettingsEmpty>
        ) : (
          props.roles.map((role) => (
            <SettingsRow key={role.id}>
              <div className="min-w-0 flex-1 space-y-2">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium text-foreground">
                    {role.name}
                  </p>
                  {props.allowDefault && role.isDefault ? (
                    <DefaultRoleTag />
                  ) : null}
                </div>
                <DealPermissionsSummary
                  permissions={role.permissionLevels}
                  roleType={role.type}
                />
              </div>
              <div className="flex shrink-0 items-center gap-1">
                {props.allowDefault && !role.isDefault ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={updateRole.isPending}
                    onClick={() => makeDefault(role)}
                  >
                    Make default
                  </Button>
                ) : null}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setEditingRole(role)}
                >
                  Edit
                </Button>
              </div>
            </SettingsRow>
          ))
        )}
      </SettingsCard>

      <CreateDealRoleModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        roleType={props.roleType}
        dealId={props.dealId}
      />

      {editingRole ? (
        <EditDealRoleModal
          open
          role={editingRole}
          onClose={() => setEditingRole(null)}
        />
      ) : null}
    </>
  );
}
